import type { AiInvolvement, PeopleConsent, RightsBasis } from './types.js';

/**
 * Shared compliance-form building blocks for `MediaUploadPanel` (batch
 * upload) and `MediaDetailDialog` (single-image edit, task 27) — both forms
 * ask the exact same two questions and reveal the exact same conditional
 * fields off the exact same answers. Pulled out here so the two surfaces
 * cannot drift apart: a change to "when do we ask for the stock territory"
 * previously would have had to land in two files by hand.
 */

export const RIGHTS_BASIS_LABEL: Record<RightsBasis, string> = {
  client_provided: 'Ügyféltől kaptuk',
  stock: 'Stockról',
  own_shoot: 'Saját fotózás',
  ai_generated: 'AI generálta',
  written_permission: 'Írásos engedéllyel',
  unknown: 'Nem ismert',
};

export const AI_INVOLVEMENT_LABEL: Record<AiInvolvement, string> = {
  none: 'Nincs AI-érintettség',
  ai_edited: 'AI szerkesztette',
  ai_generated: 'AI generálta',
  unknown: 'Nem ismert',
};

export const PEOPLE_CONSENT_LABEL: Record<PeopleConsent, string> = {
  none: 'Nincs hozzájárulás',
  confirmed: 'Hozzájárulás megerősítve',
  crowd_exception: 'Tömegfelvétel — egyéni hozzájárulás nem kell',
};

/** Only client-supplied images carry a confirmation reference. */
export function showsRightsConfirmedRef(rightsBasis: RightsBasis): boolean {
  return rightsBasis === 'client_provided';
}

/**
 * Shared hint under the reference field, both surfaces. Says what the field
 * is actually FOR — name the real, dated confirmation being relied on — and
 * distinguishes it from what saving it does: the reference carries the real
 * date (of the client's e-mail, say); the save records the moment someone
 * wrote that down as `rightsConfirmedAt` (see `buildNewRightsConfirmation`).
 */
export const RIGHTS_CONFIRMED_REF_HINT =
  'Nevezd meg az írásos megerősítést, amire támaszkodsz — pl. az ügyféltől kapott e-mail dátuma és tárgya. A hivatkozás az eredeti dátumot viszi; a mentés azt rögzíti, mikor írtuk le.';

/** Only stock images carry platform/territory/expiry. */
export function showsStockFields(rightsBasis: RightsBasis): boolean {
  return rightsBasis === 'stock';
}

/** The "what changed" note only matters once AI touched the image. */
export function showsAiChangeNote(aiInvolvement: AiInvolvement): boolean {
  return aiInvolvement === 'ai_edited' || aiInvolvement === 'ai_generated';
}

/**
 * The confirmation pair as it goes to the API — and the shared history of
 * both builders below.
 *
 * The client-supplied rights basis is the only one with a confirmation
 * reference at all — and the compliance engine's `client_unconfirmed`
 * blocker (`packages/core/src/media-compliance/evaluate.ts`) clears off the
 * `rightsConfirmedAt` TIMESTAMP, not off the reference text. Before fix
 * round 1 of task 27, neither the upload panel nor the detail dialog ever
 * sent that timestamp — a client-supplied image could never be unblocked
 * through either form, no matter what a colleague typed into the reference
 * field.
 *
 * The reference is the human-readable carrier ("e-mail 09-08, tárgy:
 * fotók"); this is what turns naming it into an actual recorded
 * confirmation — the moment someone names a real reference IS the moment,
 * from the app's point of view, that the confirmation is on file. Clearing
 * the reference clears the timestamp right back, so an edit that empties
 * the field re-blocks the image instead of leaving a stale "confirmed" date
 * with nothing behind it.
 *
 * Which builder a surface takes is the finding-14 distinction, and it is
 * stated in the function names on purpose rather than hidden in an optional
 * argument: an upload or a bulk write has no previous value to keep, the
 * detail dialog does.
 */
export interface RightsConfirmation {
  rightsConfirmedRef: string | null;
  rightsConfirmedAt: string | null;
}

/** The reference text as the record carries it today, trimmed for comparison. */
function normalizeRef(reference: string | null): string {
  return (reference ?? '').trim();
}

/**
 * The confirmation pair for a surface that has NO stored previous value:
 * `MediaUploadPanel` (the row does not exist yet) and `MediaBulkBar` (many
 * rows at once, each with its own history the form never loaded). "Now" is
 * the only honest answer on both — the reference is being written for the
 * first time as far as this form can tell.
 *
 * `MediaDetailDialog` must NOT use this one: see
 * `buildEditedRightsConfirmation`.
 */
export function buildNewRightsConfirmation(
  rightsBasis: RightsBasis,
  reference: string,
): RightsConfirmation {
  if (!showsRightsConfirmedRef(rightsBasis)) {
    return { rightsConfirmedRef: null, rightsConfirmedAt: null };
  }
  const trimmed = reference.trim();
  return {
    rightsConfirmedRef: trimmed.length > 0 ? trimmed : null,
    rightsConfirmedAt: trimmed.length > 0 ? new Date().toISOString() : null,
  };
}

/**
 * The confirmation pair for `MediaDetailDialog`, the one surface that edits a
 * single, already-loaded record — so it knows what is stored, and must keep it.
 *
 * `rightsConfirmedAt` records WHEN the confirmation reached the system. It is
 * audit data: re-stamping it on an unrelated save (fixing a caption or a tag
 * on a client-supplied photo) throws away the original date and replaces it
 * with today, which is exactly what the field must never say. So a fresh
 * timestamp is produced only when the reference TEXT actually changed —
 * a new reference is a new confirmation. Whitespace-only differences do not
 * count as a change.
 *
 * A record that carries a reference but no timestamp (written before this
 * field existed) still gets one, otherwise the `client_unconfirmed` blocker
 * could never be cleared by saving the dialog.
 *
 * Clearing the reference clears both, on every surface: withdrawing the
 * confirmation re-blocks the image rather than leaving a stale date behind.
 */
export function buildEditedRightsConfirmation(
  rightsBasis: RightsBasis,
  reference: string,
  stored: RightsConfirmation,
): RightsConfirmation {
  const fresh = buildNewRightsConfirmation(rightsBasis, reference);
  if (fresh.rightsConfirmedRef === null) return fresh;
  const unchanged = fresh.rightsConfirmedRef === normalizeRef(stored.rightsConfirmedRef);
  if (unchanged && stored.rightsConfirmedAt !== null) {
    return {
      rightsConfirmedRef: fresh.rightsConfirmedRef,
      rightsConfirmedAt: stored.rightsConfirmedAt,
    };
  }
  return fresh;
}

/**
 * The "is there a recognisable person on this photo" answer has THREE
 * states, not two, and the third one is the point. `null` — nobody has
 * answered yet — is what keeps the compliance engine's `people_unknown`
 * blocker alive (`packages/core/src/media-compliance/evaluate.ts`), so an
 * untriaged image stays out of posts until a human actually says.
 *
 * A checkbox cannot express that: unchecked reads as `false`, and `false` is
 * a legal statement ("there is nobody recognisable on this image") that
 * clears the blocker. Before this fix every form defaulted to unchecked, so
 * saving a caption on an old photo — or setting the rights basis on 200
 * photos at once — silently made that statement on the colleague's behalf.
 * Fail-closed means the "I haven't answered" state must be representable in
 * the form, must be the DEFAULT on upload, and must survive a save.
 *
 * The server schema already carries this distinction (`.default(null)`, the
 * Ruling 7 fix); these two converters are the client-side half of it.
 */
export type PeopleRecognizableChoice = 'yes' | 'no' | 'unknown';

export const PEOPLE_RECOGNIZABLE_CHOICES: readonly PeopleRecognizableChoice[] = [
  'yes',
  'no',
  'unknown',
];

export const PEOPLE_RECOGNIZABLE_LABEL: Record<PeopleRecognizableChoice, string> = {
  yes: 'Van felismerhető személy',
  no: 'Nincs felismerhető személy',
  unknown: 'Nem tudom',
};

/** The question above the three options, on every surface that asks it. */
export const PEOPLE_RECOGNIZABLE_LEGEND = 'Felismerhető személy a képen';

/** Form answer → the column value. `unknown` is `null`, never `false`. */
export function toPeopleRecognizable(choice: PeopleRecognizableChoice): boolean | null {
  if (choice === 'yes') return true;
  if (choice === 'no') return false;
  return null;
}

/** Stored value → the option the form opens on. `null` → "Nem tudom". */
export function fromPeopleRecognizable(value: boolean | null): PeopleRecognizableChoice {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'unknown';
}
